/** @jsxImportSource @emotion/react */
import { Global } from "@emotion/react";
import { DateInputValue, Input } from "basis";
import { SvgInline } from "components/SvgInline/SvgInline";
import { isValid, parse, parseISO } from "date-fns";
import { useActiveElement } from "library/hooks/useActiveElement";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  DayClickEventHandler,
  DayPicker,
  useInput,
  UseInputOptions,
} from "react-day-picker";
import "react-day-picker/style.css";

const Styles = () => (
  <Global
    styles={{
      ".rdp-day": {
        borderRadius: "0",
        color: "#006aff",
        fontWeight: 500,
      },

      ".rdp-day:hover": {
        border: "1px solid #006aff",
        cursor: "default",
      },

      ".rdp-cell": {
        border: "1px solid #e9ebec",
      },

      ".rdp-nav > button": {
        width: "44px",
        height: "44px",
        borderRadius: "0",
      },

      ".rdp-button[disabled]": {
        opacity: 1,
        color: "#8e959b",
        fontWeight: 300,
      },

      ".rdp-button[disabled]:hover": {
        border: "none",
      },

      ".rdp-caption_label": {
        textAlign: "inherit",
        fontFamily: "'Montserrat',sans-serif",
        fontWeight: 600,
        fontSize: "20px",
        lineHeight: "28px",
        letterSpacing: "-0.52px",
      },
    }}
  />
);

type DatePickerProps = {
  label: string;
  helpText?: string;
  value?: DateInputValue;
  fromDate?: Date;
  toDate?: Date;
  error?: string;
  onChange?: (value: DateInputValue) => void;
  onBlur?: () => void;
  onFocus?: () => void;
};

const DATE_FORMAT = "dd/MM/yyyy";

const EMPTY_DATE_VALUE = {
  day: "",
  month: "",
  year: "",
};

const getDateValue = (date: Date): DateInputValue =>
  isValid(date)
    ? {
        day: `${date.getDate()}`,
        month: `${date.getMonth() + 1}`,
        year: `${date.getFullYear()}`,
      }
    : EMPTY_DATE_VALUE;

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (props, ref) => {
    const {
      label,
      error,
      onBlur,
      value = EMPTY_DATE_VALUE,
      helpText,
      fromDate,
      toDate,
    } = props;

    const { day, month, year } = value;
    const selectedDate = useMemo(() => {
      const parsedDate = parseISO(
        `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
      );
      return isValid(parsedDate) ? parsedDate : undefined;
    }, [day, month, year]);

    const options: UseInputOptions = {
      defaultSelected: selectedDate,
      fromDate,
      toDate,
      format: DATE_FORMAT,
    };

    const input = useInput(options);
    const [isOpen, setOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const activeElement = useActiveElement();

    const onFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
      input.fieldProps.onFocus(e);
      setOpen(true);
      props.onFocus?.();
    };

    const onChange: React.FocusEventHandler<HTMLInputElement> = (e) => {
      input.fieldProps.onChange(e);
      const date = parse(e.target.value, DATE_FORMAT, new Date());
      props.onChange?.(getDateValue(date));
    };

    const onDayClick: DayClickEventHandler = (...args) => {
      const [selectedDay, modifiers] = args;
      input.dayPickerProps.onDayClick(...args);
      if (modifiers.selected) {
        props.onChange?.(EMPTY_DATE_VALUE);
      } else {
        setOpen(false);
        const val = getDateValue(selectedDay);
        props.onChange?.(val);
      }
    };

    useEffect(() => {
      if (!activeElement) {
        return;
      }

      if (wrapperRef.current?.contains(activeElement)) {
        if (!isOpen) {
          setOpen(true);
        }
      } else if (isOpen) {
        setOpen(false);
        onBlur?.();
      }
    }, [activeElement, isOpen, onBlur]);

    return (
      <>
        <Styles />
        <div ref={wrapperRef}>
          <div
            css={{
              position: "relative",
            }}
          >
            <Input
              label={label}
              {...input.fieldProps}
              error={error}
              helpText={helpText}
              placeholder="DD/MM/YYYY"
              ref={inputRef}
              onFocus={onFocus}
              onChange={onChange}
            />
            <div
              css={{
                position: "absolute",
                width: "32px",
                height: "32px",
                bottom: error ? "36px" : helpText ? "56px" : "8px",
                right: "8px",
              }}
              role="button"
              tabIndex={-1}
              aria-hidden
              onClick={() => inputRef.current?.focus()}
            >
              <SvgInline name="icon-calendar" />
            </div>
          </div>
          {isOpen && (
            <div css={{ position: "relative", marginTop: "4px" }}>
              <div
                css={{
                  position: "absolute",
                  zIndex: 2,
                  backgroundColor: "white",
                  marginTop: error ? "0" : helpText ? "-48px" : "0",
                  boxShadow: `0 1px 4px 0 rgba(0, 0, 0, 0.25)`,
                }}
              >
                <DayPicker {...input.dayPickerProps} onDayClick={onDayClick} />
              </div>
            </div>
          )}
        </div>
      </>
    );
  },
);
